import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'shipin');

  const category = useMemo(
    () => categories.find((c) => c.slug === activeSlug),
    [activeSlug]
  );

  const categoryArticles = useMemo(
    () => articles.filter((a) => a.categorySlug === activeSlug),
    [activeSlug]
  );

  return (
    <>
      <SEOHead
        title={`${category?.name || '分类'} - 久久在线`}
        description={category?.description || '久久在线内容分类，发现更多精彩内容'}
        keywords={`久久在线,${category?.name || ''},在线内容,久久在线资源`}
        canonicalUrl={`/category/${activeSlug}`}
        ogType="website"
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="px-4 md:px-6 py-8 bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: category?.name || '分类' },
              ]}
            />
          </div>
        </div>

        <div className="px-4 md:px-6 py-10">
          <div className="max-w-7xl mx-auto">
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2">
              {category?.name}频道
            </h1>
            <p className="text-foreground-500 text-sm">{category?.description}</p>
          </div>
        </div>

        <div className="px-4 md:px-6 pb-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-foreground-900 text-sm leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="mt-2 text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-4 mt-3 text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i> {(article.views / 10000).toFixed(1)}万
                        </span>
                        <span>{article.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-inbox-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}